import { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsCalendar, BsCheck, BsThreeDots } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import IconButton from '#globalComponents/IconButton/IconButton';
import { getInitials } from '#lib/textUtils';

import { isOverdue, isSending } from '../../../utils/activityStatus';
import CallReachedPopover from '../../common/CallReachedConfirm/CallReachedPopover';

import PlannedActionsMenu from './PlannedActionsMenu';
import ReassignPopover from './ReassignPopover';

function formatDateTime(iso) {
	if (!iso) return '';
	try {
		return new Intl.DateTimeFormat('de-DE', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(new Date(iso));
	} catch {
		return '';
	}
}

const TextArea = ({ activity, overdue }) => (
	<>
		<div className="text-md font-semibold text-text truncate">{activity.title}</div>
		{activity.body && <p className="text-xs text-text mt-0.5 line-clamp-1">{activity.body}</p>}
		{activity.dueAt && (
			<div className={`text-xs flex items-center gap-1 mt-1 ${overdue ? 'text-warning-500' : 'text-text'}`}>
				<BsCalendar className="w-2.5 h-2.5" /> {formatDateTime(activity.dueAt)}
			</div>
		)}
	</>
);

TextArea.propTypes = {
	activity: PropTypes.object.isRequired,
	overdue: PropTypes.bool,
};

const PlannedActivityItem = ({
	activity,
	Icon,
	isCallType,
	assignedMember,
	members,
	onStartEdit,
	onCheckboxClick,
	onCallReachedSelect,
	onReassign,
	onCancelAutomated,
	onDelete,
}) => {
	const [actionsOpen, setActionsOpen] = useState(false);
	const [reassignOpen, setReassignOpen] = useState(false);
	const [callReachedOpen, setCallReachedOpen] = useState(false);
	const [checkboxEl, setCheckboxEl] = useState(null);
	const actionsButtonRef = useRef(null);

	const overdue = isOverdue(activity);
	const sending = isSending(activity);
	const isCampaign = Boolean(activity.channelPayload?.campaignId);
	const canEdit = !activity.isAutomated && !isCampaign;

	const handleCheckbox = () => {
		if (isCallType) {
			setCallReachedOpen(true);
		} else {
			onCheckboxClick(activity);
		}
	};

	const handleCallReachedSelectInternal = (reached) => {
		onCallReachedSelect(activity, reached);
		setCallReachedOpen(false);
	};

	return (
		<Card
			className={`relative group transition-colors hover:border-primary-200 ${
				overdue ? 'border-warning-500/40 bg-warning-200/30' : ''
			}`}
		>
			<div className="flex items-center gap-2.5">
				{activity.isAutomated ? (
					<span
						className="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-200 text-primary-700"
						title={sending ? 'Versand läuft' : 'Automatisierter Versand'}
					>
						{sending ? 'Sendet…' : 'Auto'}
					</span>
				) : (
					<>
						<button
							type="button"
							ref={setCheckboxEl}
							onClick={handleCheckbox}
							className="group/check flex-shrink-0 w-4 h-4 rounded-full border-[1.5px] border-bgBase-600 bg-white hover:border-success-500 hover:bg-success-500 transition-colors grid place-items-center"
							title="Als erledigt markieren"
						>
							<BsCheck className="w-3 h-3 text-transparent group-hover/check:text-white transition-colors" />
						</button>
						{callReachedOpen && (
							<CallReachedPopover
								anchorRef={{ current: checkboxEl }}
								onSelect={handleCallReachedSelectInternal}
								onCancel={() => setCallReachedOpen(false)}
							/>
						)}
					</>
				)}
				<Icon className="w-3.5 h-3.5 text-bgBase-800 flex-shrink-0" />
				{canEdit ? (
					<button
						type="button"
						onClick={() => onStartEdit(activity)}
						className="flex-1 min-w-0 text-left bg-transparent border-0 p-0 cursor-pointer"
						title="Bearbeiten"
					>
						<TextArea activity={activity} overdue={overdue} />
					</button>
				) : (
					<div className="flex-1 min-w-0">
						<TextArea activity={activity} overdue={overdue} />
					</div>
				)}
				{assignedMember && (
					<span
						className="w-5 h-5 rounded-full bg-bgBase-500 text-[9px] font-semibold text-text grid place-items-center flex-shrink-0"
						title={assignedMember.fullName ?? assignedMember.email}
					>
						{getInitials(assignedMember.fullName ?? assignedMember.email)}
					</span>
				)}
				<div className="relative">
					<IconButton
						ref={actionsButtonRef}
						icon={BsThreeDots}
						label="Aktionen"
						onClick={() => setActionsOpen((v) => !v)}
						className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-[color,background-color,opacity]"
					/>
					{actionsOpen && (
						<PlannedActionsMenu
							activity={activity}
							isCampaign={isCampaign}
							onEdit={() => {
								setActionsOpen(false);
								onStartEdit(activity);
							}}
							onComplete={() => {
								setActionsOpen(false);
								handleCheckbox();
							}}
							onReassign={() => {
								setActionsOpen(false);
								setReassignOpen(true);
							}}
							onCancelAutomated={() => {
								setActionsOpen(false);
								onCancelAutomated(activity);
							}}
							onDelete={() => {
								setActionsOpen(false);
								onDelete(activity);
							}}
							onClose={() => setActionsOpen(false)}
							anchorRef={actionsButtonRef}
						/>
					)}
					{reassignOpen && (
						<ReassignPopover
							members={members}
							currentUserId={activity.userId}
							onSelect={(userId) => {
								onReassign(activity.id, userId);
								setReassignOpen(false);
							}}
							onRemove={
								activity.userId
									? () => {
											onReassign(activity.id, null);
											setReassignOpen(false);
										}
									: undefined
							}
							onClose={() => setReassignOpen(false)}
							anchorRef={actionsButtonRef}
						/>
					)}
				</div>
			</div>
		</Card>
	);
};

PlannedActivityItem.propTypes = {
	activity: PropTypes.object.isRequired,
	Icon: PropTypes.elementType.isRequired,
	isCallType: PropTypes.bool.isRequired,
	assignedMember: PropTypes.object,
	members: PropTypes.array.isRequired,
	onStartEdit: PropTypes.func.isRequired,
	onCheckboxClick: PropTypes.func.isRequired,
	onCallReachedSelect: PropTypes.func.isRequired,
	onReassign: PropTypes.func.isRequired,
	onCancelAutomated: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

export default PlannedActivityItem;
